<template>
  <form @submit.prevent="submitForm" class="poa-form">
    <!-- Order summary -->
    <div class="poa-summary">
      <div class="sum-grid">
        <div class="sum-row">
          <span class="sum-label">Receipt</span>
          <span class="sum-value mono upper">{{ item.receiptId }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Order ID</span>
          <span class="sum-value mono break">{{ item.merchantOrderId }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Account holder</span>
          <span class="sum-value">{{ item.customerName || '—' }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Assigned to</span>
          <span class="sum-value">{{ item.validatorUsername || '—' }}</span>
        </div>
        <div v-if="item.accountNumber" class="sum-row">
          <span class="sum-label">Account no.</span>
          <span class="sum-value mono">{{ item.accountNumber }}</span>
        </div>
        <div v-if="item.ifsc" class="sum-row">
          <span class="sum-label">IFSC</span>
          <span class="sum-value mono">{{ item.ifsc }}</span>
        </div>
        <div v-if="item.bankName" class="sum-row">
          <span class="sum-label">Bank</span>
          <span class="sum-value">{{ item.bankName }}</span>
        </div>
        <div v-if="item.customerUPIID" class="sum-row">
          <span class="sum-label">Customer UPI</span>
          <span class="sum-value mono">{{ item.customerUPIID }}</span>
        </div>
        <div v-if="isAdmin && item.validatorUPIID" class="sum-row">
          <span class="sum-label">Assigned UPI</span>
          <span class="sum-value mono break">{{ item.validatorUPIID }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Created</span>
          <span class="sum-value">{{ formatDate }}</span>
        </div>
      </div>
      <div class="poa-amount-block">
        <div class="amount-label">
          {{ alreadyPaidAmount > 0 ? 'Amount to pay' : 'Amount to approve' }}
        </div>
        <div class="amount-value">₹{{ formatMoney(getAmount) }}</div>
      </div>
    </div>

    <!-- Approval type. Partial tab hidden when this branch's setting
         (can_partial_approve_payout) is OFF — full-only. Admin/vendor
         always see it. Server re-checks regardless. -->
    <div class="poa-approval-type" v-if="canPartialApprove">
      <label class="type-label">Approval type</label>
      <div class="type-tabs">
        <label class="type-tab" :class="{ 'is-active': approvalType === 'full' }">
          <input type="radio" v-model="approvalType" value="full" />
          <span>Full approval</span>
        </label>
        <label class="type-tab" :class="{ 'is-active': approvalType === 'partial' }">
          <input type="radio" v-model="approvalType" value="partial" />
          <span>Partial approval</span>
        </label>
      </div>
    </div>

    <!-- Multi-branch progress -->
    <!-- Only the remaining figure is shown. "Already paid" was removed on
         purpose: this form is branch-only, and showing paid + remaining
         side by side let a branch reconstruct the original total by simple
         addition — the exact number the branch must never act on. The
         policy is: a branch on a partially-settled open order sees ONLY
         what is left to pay, at every stage. -->
    <div v-if="alreadyPaidAmount > 0" class="poa-progress">
      <div class="progress-title">Order partially settled by previous branch(es)</div>
      <div class="progress-row">
        <span>Remaining to settle</span>
        <strong class="text-success">₹{{ getRemainingAmount }}</strong>
      </div>
    </div>

    <!-- Partial amount input -->
    <div v-if="approvalType === 'partial'" class="field" :class="{ 'field-invalid': !approvalAmount.isValid }">
      <label>Approval amount</label>
      <div class="input-wrap">
        <span class="input-prefix">₹</span>
        <input
          type="number"
          step="0.01"
          v-model.number="approvalAmount.val"
          placeholder="Enter amount to approve"
          class="input has-prefix"
          @focus="clearValidity('approvalAmount')"
        />
      </div>
      <p v-if="!approvalAmount.isValid" class="field-error">{{ approvalAmount.msg }}</p>
      <div class="field-hints">
        <p>Remaining: <strong>₹{{ getRemainingAmount }}</strong></p>
        <p>Min partial: <strong>₹{{ partialFloor.toFixed(2) }}</strong> — waived when closing the order.</p>
      </div>
    </div>

    <!-- UTR tag input -->
    <div class="field" :class="{ 'field-invalid': !utrField.isValid }">
      <label>
        UTR code(s)
        <span class="label-hint">— Enter or comma to add ({{ utrs.length }}/{{ MAX_UTRS }})</span>
      </label>
      <div class="tag-input" :class="{ 'tag-input-invalid': !utrField.isValid }">
        <span v-for="(utr, i) in utrs" :key="utr + i" class="utr-tag">
          <span class="mono">{{ utr }}</span>
          <button type="button" @click.stop="removeUtr(i)" class="utr-tag-close" title="Remove">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
            </svg>
          </button>
        </span>
        <input
          type="text"
          v-model.trim="utrInput"
          @keydown.enter.prevent="addUtr"
          @keydown="onUtrKey"
          @blur="addUtr"
          :placeholder="utrs.length === 0 ? 'Enter UTR' : (utrs.length >= MAX_UTRS ? 'Max reached' : 'Add another UTR')"
          :disabled="utrs.length >= MAX_UTRS"
          class="tag-input-field"
        />
      </div>
      <p v-if="!utrField.isValid" class="field-error">{{ utrField.msg }}</p>
    </div>

    <!-- Screenshots drop-zone -->
    <div class="field">
      <label>
        Payment screenshots <span class="req">*</span>
        <span class="label-hint">— one slip per UTR ({{ screenshots.length }}/{{ utrs.length || '?' }}, max {{ MAX_SCREENSHOTS }})</span>
      </label>
      <div
        @drop.prevent="handleDrop"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        :class="['drop-zone', { 'is-dragging': dragOver, 'has-file': screenshots.length > 0 }]"
        @click="screenshots.length < MAX_SCREENSHOTS && $refs.fileInput.click()"
      >
        <div v-if="screenshots.length === 0" class="drop-empty">
          <div class="drop-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M12 3v14M6 9l6-6 6 6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="drop-copy">
            <div class="drop-title">Drop images here or click to browse</div>
            <div class="drop-sub">Multiple files supported · up to {{ MAX_SCREENSHOTS }}</div>
          </div>
        </div>
        <div v-else class="drop-selected">
          <div class="drop-selected-head">✓ {{ screenshots.length }} file(s) selected</div>
          <div class="chips">
            <div v-for="(file, index) in screenshots" :key="index" class="file-chip">
              <span class="file-chip-name">{{ file.name }}</span>
              <button type="button" @click.stop="removeScreenshot(index)" class="file-chip-close" title="Remove">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <p v-if="screenshotError" class="field-error">{{ screenshotError }}</p>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        multiple
        @change="handleFileUpload"
        class="hidden-input"
      />
    </div>

    <div class="form-actions">
      <button
        type="submit"
        class="btn btn-success"
        :disabled="isLoading"
      >
        <span v-if="!isLoading" class="btn-content">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ approvalType === 'partial' ? 'Partial approve' : 'Confirm approval' }}
        </span>
        <span v-else class="btn-loading">
          <span class="spinner-mini"></span>
          Confirming…
        </span>
      </button>
    </div>
  </form>
</template>

<script>
export default {
  name: "pay-out-approve-form",
  props: ["item"],
  emits: ["form-response"],
  data() {
    return {
      approvalType: 'full',
      // UTR-tag input: `utrs` is the committed list of tags; `utrInput` is
      // the in-progress text the user is typing before pressing Enter / `,`.
      utrs: [],
      utrInput: "",
      utrField: {
        isValid: true,
        msg: "",
      },
      MAX_UTRS: 7,
      MAX_SCREENSHOTS: 7,
      approvalAmount: {
        val: null,
        isValid: true,
        msg: "",
      },
      screenshots: [],
      screenshotError: null,
      dragOver: false,
      formIsValid: true,
      isLoading: false,
    };
  },
  computed: {

    getAmount() {
      let tempAmount = 0;
      if (this.item.is_instant_payout && this.item.payout_type == 'instant' && (this.item.instant_balance > 0)) {
        tempAmount = parseInt(this.item.instant_balance);
      } else if (this.alreadyPaidAmount > 0 && parseFloat(this.item.remaining_amount || 0) > 0) {
        // Partial settlement in progress — branch sees ONLY what this round
        // must pay (remaining), never the original total. Without this, the
        // big "Amount" header on the approval form would still read the full
        // original and tempt the branch to settle the entire amount again.
        tempAmount = parseInt(this.item.remaining_amount);
      } else {
        tempAmount = parseInt(this.item.amount);
      }
      return tempAmount;
    },
    getRemainingAmount() {
      const remaining = parseFloat(this.item.remaining_amount || this.item.amount);
      return remaining.toFixed(2);
    },
    originalAmount() {
      return parseFloat(this.item.amount || 0);
    },
    alreadyPaidAmount() {
      // Cumulative amount settled by prior branches on this order (this
      // round's branch is picking a re-pickup, not the original assignment).
      return parseFloat(this.item.approved_amount || 0);
    },
    partialFloor() {
      // 25% of the ORIGINAL order amount, rounded to 2dp.
      return Math.round(this.originalAmount * 0.25 * 100) / 100;
    },
    isAdmin() {
      return !!this.$store.getters.isAdmin;
    },
    // Partial-approve gate (20092026). Admin/vendor bypass; a subadmin is
    // gated by their can_partial_approve_payout setting. UI-only — the
    // server re-checks fresh in approvePayOutOrder, so hiding the tab is a
    // convenience, not the security boundary.
    canPartialApprove() {
      const role = this.$store.getters.role;
      if (role !== 'subadmin') return true;
      return this.$store.getters.canPartialApprovePayout;
    },
    formatDate() {
      if (!this.item.createdAt) return '';
      const date = new Date(this.item.createdAt);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const ampm = hours >= 12 ? "pm" : "am";
      return `${day}-${month}-${year} ${hours % 12}:${minutes}:${seconds} ${ampm}`;
    },
  },
  methods: {
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    // Commit pending text as a UTR tag. Triggered by Enter, comma, and blur.
    // Splits on commas in case the user pastes "UTR1,UTR2" all at once.
    addUtr() {
      const raw = (this.utrInput || "").trim();
      if (!raw) return;
      const candidates = raw.split(",").map((s) => s.trim()).filter(Boolean);
      for (const u of candidates) {
        if (this.utrs.length >= this.MAX_UTRS) break;
        if (this.utrs.includes(u)) continue; // dedupe
        this.utrs.push(u);
      }
      this.utrInput = "";
      this.utrField.isValid = true;
    },
    onUtrKey(e) {
      // Comma also commits the current input, mirroring the Enter binding.
      if (e.key === ',') {
        e.preventDefault();
        this.addUtr();
      } else if (e.key === 'Backspace' && !this.utrInput && this.utrs.length) {
        // Backspace on empty input pops the last tag (common tag-input UX).
        this.utrs.pop();
      }
    },
    removeUtr(index) {
      this.utrs.splice(index, 1);
    },
    handleDrop(event) {
      this.dragOver = false;
      const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      this.acceptScreenshots(files);
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      this.acceptScreenshots(files);
      // Reset input so same file can be added again if needed
      this.$refs.fileInput.value = '';
    },
    acceptScreenshots(incoming) {
      const room = this.MAX_SCREENSHOTS - this.screenshots.length;
      if (room <= 0) {
        this.screenshotError = `Max ${this.MAX_SCREENSHOTS} screenshots already added.`;
        return;
      }
      if (incoming.length > room) {
        this.screenshotError = `Only the first ${room} file(s) were added — limit is ${this.MAX_SCREENSHOTS}.`;
      } else {
        this.screenshotError = null;
      }
      this.screenshots = [...this.screenshots, ...incoming.slice(0, room)];
    },
    removeScreenshot(index) {
      this.screenshots.splice(index, 1);
      this.screenshotError = null;
    },
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    validateForm() {
      this.formIsValid = true;
      // Flush any text the user typed but didn't commit before hitting submit.
      if (this.utrInput && this.utrInput.trim()) this.addUtr();
      if (this.utrs.length === 0) {
        this.utrField.isValid = false;
        this.utrField.msg = "At least one UTR is required.";
        this.formIsValid = false;
      } else if (this.utrs.length > this.MAX_UTRS) {
        this.utrField.isValid = false;
        this.utrField.msg = `Maximum ${this.MAX_UTRS} UTRs allowed.`;
        this.formIsValid = false;
      }
      if (this.approvalType === 'partial') {
        if (!this.approvalAmount.val || this.approvalAmount.val <= 0) {
          this.approvalAmount.isValid = false;
          this.approvalAmount.msg = "Approval amount must be greater than zero.";
          this.formIsValid = false;
        } else {
          const remaining = parseFloat(this.item.remaining_amount || this.item.amount);
          const r2 = (n) => Math.round(n * 100) / 100;
          const settle = r2(this.approvalAmount.val);
          const remaining2 = r2(remaining);
          if (settle > remaining2) {
            this.approvalAmount.isValid = false;
            this.approvalAmount.msg = `Amount cannot exceed remaining amount (₹${remaining}).`;
            this.formIsValid = false;
          } else if (settle < this.partialFloor && settle < remaining2) {
            // Floor (25% of original) is waived only when this settlement is
            // the order's closing payment. Anything less and not-closing is
            // rejected by both UI and backend.
            this.approvalAmount.isValid = false;
            this.approvalAmount.msg =
              `Minimum partial settlement is ₹${this.partialFloor.toFixed(2)}. To settle less, switch to Full Approval (closes the order).`;
            this.formIsValid = false;
          }
        }
      }
      // One payment slip per UTR. Branch attaches a screenshot for every UTR
      // entered so the merchant callback carries a 1-to-1 proof set.
      if (this.utrs.length > 0 && this.screenshots.length !== this.utrs.length) {
        this.screenshotError = this.screenshots.length === 0
          ? `Please attach ${this.utrs.length} payment screenshot${this.utrs.length === 1 ? '' : 's'} (one per UTR).`
          : `Screenshot count (${this.screenshots.length}) must match UTR count (${this.utrs.length}).`;
        this.formIsValid = false;
      }
    },
    async submitForm() {
      this.validateForm();
      if (!this.formIsValid) {
        return;
      }

      const formData = new FormData();
      formData.append('paymentStatus', this.approvalType === 'partial' ? 'partially_approved' : 'approved');
      // Backend accepts comma-separated UTRs (max 7). Sent as one field for
      // backwards compatibility with the original single-UTR signature.
      formData.append('transactionID', this.utrs.join(','));

      if (this.approvalType === 'partial') {
        formData.append('approvalAmount', this.approvalAmount.val);
      }

      // Append all screenshots
      for (const file of this.screenshots) {
        formData.append('screenshots', file, file.name);
      }

      if (this.item.payout_type == 'instant') {
        formData.append('payout_type', 'instant');
        formData.append('instant_balance', this.item.instant_balance);
      }

      this.isLoading = true;

      // Capture the submission type BEFORE resetting form state, so the
      // success message reflects what the user just did (partial vs full).
      const wasPartial = this.approvalType === 'partial';

      try {
        await this.$store.dispatch("payin/approvePayOutOrder", {
          refID: this.item.refID,
          formData: formData
        });
        this.utrs = [];
        this.utrInput = "";
        this.utrField = { isValid: true, msg: "" };
        this.approvalAmount = {
          val: null,
          isValid: true,
          msg: "",
        };
        this.approvalType = 'full';
        this.screenshots = [];
        this.screenshotError = null;
        this.$emit(
          "form-response",
          "success",
          wasPartial
            ? "Partial settlement accepted. Order returned to the available list for the remaining amount."
            : "Pay Out Order approved successfully."
        );
      } catch (error) {
        this.error = error.message || "Something failed!";
        this.$emit("form-response", "failed", this.error);
      }

      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.poa-form { display: flex; flex-direction: column; gap: 14px; }

.poa-summary {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  padding: 14px;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 12px;
  align-items: center;
}
.sum-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 20px;
  min-width: 0;
}
.sum-row {
  display: grid;
  grid-template-columns: 108px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 12.5px;
  min-width: 0;
}
.sum-label { color: #94a3b8; font-weight: 500; }
.sum-value { color: #1b2a47; font-weight: 600; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.sum-value.mono { font-family: "SFMono-Regular", Menlo, monospace; font-size: 11.5px; color: #334155; letter-spacing: 0.02em; }
.sum-value.upper { text-transform: uppercase; }
.sum-value.break { word-break: break-all; overflow-wrap: anywhere; }

.poa-amount-block {
  padding: 10px 16px;
  background: linear-gradient(135deg, #059669, #047857);
  border-radius: 12px;
  color: #fff;
  text-align: right;
  box-shadow: 0 6px 16px -8px rgba(5, 150, 105, 0.55);
  min-width: 160px;
}
.amount-label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 700;
}
.amount-value {
  font-size: 20px;
  font-weight: 800;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.1;
}

/* Approval type toggle */
.poa-approval-type { display: flex; flex-direction: column; gap: 6px; }
.type-label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}
.type-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.type-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}
.type-tab:hover { border-color: #cbd5e1; background: #fbfaf7; color: #1b2a47; }
.type-tab.is-active {
  background: #eef2f7;
  border-color: #1B2A47;
  color: #1B2A47;
}
.type-tab input { accent-color: #1B2A47; }

/* Progress panel */
.poa-progress {
  background: #dbeafe;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.progress-title {
  font-size: 12px;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 2px;
}
.progress-row {
  display: flex;
  justify-content: space-between;
  font-size: 12.5px;
  color: #334155;
}
.progress-row strong { font-weight: 700; font-variant-numeric: tabular-nums; }
.text-info { color: #1e40af; }
.text-success { color: #047857; }

/* Fields */
.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.label-hint {
  font-weight: 400;
  color: #94a3b8;
  font-size: 11.5px;
  margin-left: 4px;
}
.req { color: #dc2626; }

.input-wrap { position: relative; display: flex; align-items: center; }
.input-prefix {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}
.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input.has-prefix { padding-left: 26px; }
.input:focus {
  border-color: #059669;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(5, 150, 105, 0.10);
}
.field-invalid .input {
  border-color: #fca5a5;
  background: #fef2f2;
}
.field-hints {
  margin-top: 6px;
  font-size: 11.5px;
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.field-hints p { margin: 0; }
.field-hints strong { color: #1b2a47; font-weight: 700; font-variant-numeric: tabular-nums; }

.field-error {
  font-size: 12px;
  color: #dc2626;
  margin: 6px 0 0;
  font-weight: 500;
}

/* Tag input (UTRs) */
.tag-input {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fbfaf7;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.tag-input:focus-within {
  border-color: #059669;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(5, 150, 105, 0.10);
}
.tag-input-invalid { border-color: #fca5a5; background: #fef2f2; }
.utr-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px 3px 8px;
  border-radius: 6px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #bfdbfe;
}
.utr-tag-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #1e40af;
  width: 18px; height: 18px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.utr-tag-close svg { width: 12px; height: 12px; }
.utr-tag-close:hover { background: #bfdbfe; color: #991b1b; }
.tag-input-field {
  flex: 1;
  min-width: 120px;
  border: none;
  background: transparent;
  outline: none;
  padding: 4px 2px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
}
.tag-input-field:disabled { color: #94a3b8; }
.mono { font-family: "SFMono-Regular", Menlo, monospace; }

/* Drop zone */
.hidden-input { display: none; }
.drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 22px 18px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fbfaf7;
}
.drop-zone:hover { border-color: #94a3b8; background: #f4f0e5; }
.drop-zone.is-dragging { border-color: #1B2A47; background: #eef2f7; }
.drop-zone.has-file { border-color: #10b981; background: #ecfdf5; }

.drop-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}
.drop-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  background: #fff;
  color: #64748b;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #e2e8f0;
}
.drop-icon svg { width: 20px; height: 20px; }
.drop-title { font-size: 12.5px; font-weight: 600; color: #1b2a47; }
.drop-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.drop-selected {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.drop-selected-head {
  font-size: 12.5px;
  font-weight: 600;
  color: #047857;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px 3px 8px;
  border-radius: 6px;
  background: #d1fae5;
  color: #047857;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #a7f3d0;
  max-width: 200px;
}
.file-chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11.5px;
}
.file-chip-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #047857;
  width: 18px; height: 18px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.file-chip-close svg { width: 12px; height: 12px; }
.file-chip-close:hover { background: #a7f3d0; color: #991b1b; }

/* Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 22px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
  letter-spacing: 0.02em;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  box-shadow: 0 8px 18px -8px rgba(5, 150, 105, 0.55);
  min-width: 200px;
}
.btn-success:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px -10px rgba(5, 150, 105, 0.65);
}
.btn-content { display: inline-flex; align-items: center; gap: 6px; }
.btn-content svg { width: 15px; height: 15px; }
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .poa-summary { grid-template-columns: 1fr; }
  .sum-grid { grid-template-columns: 1fr; }
  .poa-amount-block { text-align: center; min-width: 0; }
  .type-tabs { grid-template-columns: 1fr; }
}
</style>
